/**
 * Auth Feature Types
 *
 * Centralized type definitions for authentication-related data structures.
 * These types are used across the auth feature for API responses, Redux state, and components.
 */

import type { ErrorResponse, OrganizationPlan } from "@/shared/types/common";
import type { RepresentativePermissions } from "@/shared/types/representativePermissions";
import type { UserData } from "@/shared/types/user.types";

// Re-export shared types for internal feature use and backwards compatibility
export type { UserData } from "@/shared/types/user.types";

// =============================================================================
// AUTH API RESPONSES
// =============================================================================

/**
 * Union type for all possible authentication API responses
 */
export type AuthResponse =
  | AuthLoginResponse
  | AuthWith2FactorResponse
  | AuthErrorWithoutCellPhone
  | AuthErrorResponse;

/**
 * Successful login response with access token
 */
export type AuthLoginResponse = {
  access_token: string
  token_type: "Bearer"
  expires_in: number
  description?: string
};

/**
 * Response when 2FA verification is required
 */
export type AuthWith2FactorResponse = {
  two_factor_required: true
  challenge: string
  channel: string
  message: string
  description?: string
  code_sent_at?: number
};

/**
 * Error response when user account is missing a registered phone number
 */
export type AuthErrorWithoutCellPhone = { error: ErrorResponse & { code: "missing_cell_phone_number" } } & ErrorResponse;

/**
 * Generic authentication error response
 */
export type AuthErrorResponse = { error: ErrorResponse };

// =============================================================================
// SESSION STATE
// =============================================================================

/**
 * Redux session state for authentication
 */
export type SessionState = {
  /** Current OTP challenge ID for 2FA */
  otp_challenge: string | null
  /** User email (legacy field) */
  email: string | null
  /** Whether an authentication request is in progress */
  isAuthenticating: boolean
  /** Whether 2FA verification is required */
  isDoubleAuthRequired: boolean
  /** Whether phone registration is required */
  registerPhone: boolean
  /** User's registered cell phone */
  cell_phone: string
  /** Whether user is currently authenticated */
  isAuthenticated: boolean
  /**
   * Whether the last logout was chosen by the user (button, impersonation exit,
   * logout from another tab) of an élu who does not remember their filters:
   * the return URL then drops the filter params.
   */
  hasResetFilters?: boolean
  /** Authenticated user data */
  user: UserData | null
  /** Organization plan features */
  organizationPlan: OrganizationPlan | null
  /** Whether a fetchOrganizationPlanThunk request is currently in flight (used to dedupe concurrent fetches) */
  isFetchingOrganizationPlan: boolean
  /** Representative permissions from plan API */
  representativePermissions: RepresentativePermissions | null
  /** Current error state */
  error: ErrorResponse | null
  /** 2FA message from server */
  message: string | null
  /** Error/info description */
  description: string | null
  /** OAuth grant type (password) */
  grant_type?: string
  /** Username for pending authentication */
  username: string | null
  /** Password for pending authentication (stored for 2FA flow) */
  password: string | null
  /** OAuth scope */
  scope?: string
  /** 2FA delivery channel (sms/voice) */
  channel?: string
  /** Toast notification to display */
  toast: {
    type: "info" | "error" | "success"
    info: string
    description: string | null
  } | null
  /** Timestamp when 2FA code was sent */
  twoFaTimestamp?: number
};

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Check if response is a successful login with access token
 */
export const isAuthLoginResponse = (
  auth: AuthResponse,
): auth is AuthLoginResponse => "access_token" in auth;

/**
 * Check if 2FA verification is required
 */
export const isTwoFactorRequired = (
  auth: AuthResponse,
): auth is AuthWith2FactorResponse =>
  "two_factor_required" in auth && auth.two_factor_required === true;

/**
 * Check if user account is missing a cell phone number
 */
export const isMissingCellPhoneNumber = (
  auth: AuthResponse,
): auth is AuthErrorWithoutCellPhone =>
  "error" in auth && auth.error.code === "missing_cell_phone_number";

/**
 * Check if response contains an error
 */
export const isException = (auth: AuthResponse): auth is AuthErrorResponse =>
  "error" in auth;
